"use client";

/**
 * SearchableSelect — a single-select combobox with type-ahead filtering.
 *
 * Reuses the same body-portal + fixed-position anchoring pattern as ./select.tsx
 * so the options panel escapes ancestor `overflow: hidden/auto` clipping inside
 * modals and scroll containers (TRUS-351) and stays clickable when a Radix
 * Dialog disables outside pointer events (TRUS-365).
 *
 * Built for the State/Country member fields (TRUS-395) but intentionally generic:
 * pass any {value, label}[] and it renders a searchable dropdown. cmdk is not
 * used here — the filtering + keyboard nav are self-contained so the panel can
 * live in a portal without a cmdk context wrapper on the trigger.
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search } from 'lucide-react';
import { cn } from './utils';

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Optional heading this option sits under. Options keep their given order; a
   *  heading is drawn each time the group changes, so callers group by sorting. */
  group?: string;
  /** Secondary text shown to the right — a type, a warning, whatever disambiguates
   *  two options that would otherwise read the same. Not searched. */
  hint?: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value = '',
  onValueChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Type to search...',
  emptyText = 'No matches',
  disabled = false,
  id,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  const selectedLabel = useMemo(
    () => options.find((o) => o.value === value)?.label ?? '',
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    // Match the VALUE as well as the label. A developer or admin mapping fields knows
    // the column name (`street_address`) long before they know how it was captioned,
    // and a list of 100+ options is exactly where that matters.
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q),
    );
  }, [options, query]);

  // Anchor the panel to the trigger with fixed (viewport) coordinates so it
  // escapes ancestor overflow clipping — mirrors SelectContent in ./select.tsx.
  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const maxHeight = Math.max(160, Math.min(300, spaceBelow - 8));
    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      maxHeight,
      zIndex: 10000,
      // A modal Radix Dialog sets pointer-events:none on <body>; since this panel
      // is portaled to <body> it inherits that unless we force it back (TRUS-365).
      pointerEvents: 'auto',
    });
  }, []);

  const open = useCallback(() => {
    if (disabled) return;
    setQuery('');
    // Preselect the currently-chosen option so Enter re-picks it.
    const idx = options.findIndex((o) => o.value === value);
    setActiveIndex(idx >= 0 ? idx : 0);
    setIsOpen(true);
  }, [disabled, options, value]);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    // Focus the filter input once the panel is mounted.
    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 0);

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inPanel = panelRef.current?.contains(target);
      const inTrigger = triggerRef.current?.contains(target);
      if (!inPanel && !inTrigger) setIsOpen(false);
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, updatePosition]);

  // Keep the active option within the filtered range as the query narrows.
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(0, filtered.length - 1)));
  }, [filtered.length]);

  const commit = (optValue: string) => {
    onValueChange(optValue);
    setIsOpen(false);
    setQuery('');
    triggerRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[activeIndex];
      if (opt) commit(opt.value);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      triggerRef.current?.focus();
    }
  };

  return (
    <>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={() => (isOpen ? setIsOpen(false) : open())}
        className={cn(
          'flex items-center justify-between w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:cursor-not-allowed disabled:opacity-60',
          className,
        )}
      >
        <span className={selectedLabel ? 'text-gray-900 dark:text-gray-100 truncate' : 'text-gray-400 truncate'}>
          {selectedLabel || placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={panelRef}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            className="flex flex-col bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg dark:shadow-gray-900/50 overflow-hidden"
            style={menuStyle}
          >
            <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 px-2.5">
              <Search className="w-4 h-4 shrink-0 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIndex(0);
                }}
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
                className="w-full py-2 text-sm bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none"
              />
            </div>
            <div className="overflow-y-auto py-1" role="listbox">
              {filtered.length === 0 ? (
                <div className="px-3 py-4 text-sm text-center text-gray-400">{emptyText}</div>
              ) : (
                filtered.map((opt, idx) => {
                  const isSelected = opt.value === value;
                  const isActive = idx === activeIndex;
                  // A heading whenever the group changes. Drawn from the FILTERED list,
                  // so a search that empties a group drops its heading too rather than
                  // leaving a label with nothing under it.
                  const heading =
                    opt.group && opt.group !== filtered[idx - 1]?.group ? opt.group : null;
                  return (
                    <React.Fragment key={opt.value}>
                      {heading && (
                        <div
                          role="presentation"
                          className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500"
                        >
                          {heading}
                        </div>
                      )}
                      <div
                        role="option"
                        aria-selected={isSelected}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={() => commit(opt.value)}
                        className={cn(
                          'flex items-center justify-between px-3 py-1.5 text-sm cursor-pointer',
                          isActive
                            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-200'
                            : 'text-gray-900 dark:text-gray-100',
                        )}
                      >
                        <span className="truncate mr-2">{opt.label}</span>
                        <span className="flex shrink-0 items-center gap-2">
                          {opt.hint && (
                            <span className="text-xs text-gray-400 dark:text-gray-500">{opt.hint}</span>
                          )}
                          {isSelected && (
                            <Check className="w-4 h-4 shrink-0 text-blue-600 dark:text-blue-300" />
                          )}
                        </span>
                      </div>
                    </React.Fragment>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default SearchableSelect;
